import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, Download, Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { generateICS } from '@/lib/ics';
import { useConference } from '@/context/ConferenceContext';
import { toast } from 'sonner';

const SchedulePage = () => {
  const { shortName, loading: confLoading, importantDetails, siteConfig } = useConference();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState('1');

  // Parse conference dates from API string (e.g., "September 12-14, 2026" or "September 12, 2026")
  const parseConferenceDates = (dateString) => {
    if (!dateString) return ['September 17, 2026', 'September 18, 2026', 'September 19, 2026'];
    
    try {
      // Handle both range format (12-14) and single date format (12)
      const rangeMatch = dateString.match(/^(\w+)\s+(\d+)-(\d+),\s+(\d+)$/);
      const singleMatch = dateString.match(/^(\w+)\s+(\d+),\s+(\d+)$/);
      
      let month, startDay, endDay, year;
      
      if (rangeMatch) {
        // Range format: "September 12-14, 2026"
        [, month, startDay, endDay, year] = rangeMatch;
        startDay = parseInt(startDay);
        endDay = parseInt(endDay);
      } else if (singleMatch) {
        // Single date format: "September 12, 2026"
        [, month, startDay, year] = singleMatch;
        startDay = parseInt(startDay);
        endDay = startDay; // Same day for single date
      } else {
        return ['September 17, 2026', 'September 18, 2026', 'September 19, 2026'];
      }
      
      // Generate dates for the range
      const dates = [];
      for (let day = startDay; day <= endDay; day++) {
        dates.push(`${month} ${day}, ${year}`);
      }
      
      return dates;
    } catch (error) {
      console.error('Error parsing conference dates:', error);
      return ['September 17, 2026', 'September 18, 2026', 'September 19, 2026'];
    }
  };

  const conferenceDates = parseConferenceDates(importantDetails?.ConferenceDates);

  useEffect(() => {
    const fetchSessions = async () => {
      if (confLoading) return;

      setLoading(true);
      try {
        // Use empty array since API doesn't exist
        setSessions([]);
      } catch (error) {
        console.error('Failed to fetch sessions:', error);
        setSessions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [shortName, confLoading]);

  // Group sessions by day
  const sessionsByDay = sessions.reduce((acc, session) => {
    const day = session.day || 1;
    if (!acc[day]) acc[day] = [];
    acc[day].push(session);
    return acc;
  }, {});

  const getTrackColor = (track) => {
    if (!track) return '#6b7280';
    const trackConfig = (siteConfig?.tracks || [
      { id: 'structural', name: 'Structural Engineering', color: '#0ea5e9' },
      { id: 'geotechnical', name: 'Geotechnical Engineering', color: '#8b5cf6' },
      { id: 'transportation', name: 'Transportation Engineering', color: '#f59e0b' },
      { id: 'water', name: 'Water Resources', color: '#06b6d4' },
      { id: 'construction', name: 'Construction Management', color: '#10b981' },
      { id: 'environmental', name: 'Environmental Engineering', color: '#84cc16' },
    ]).find((t) => t.id === track);
    return trackConfig?.color || '#0ea5e9';
  };

  const getSessionTypeLabel = (type) => {
    const labels = {
      keynote: 'Keynote',
      technical: 'Technical Session',
      panel: 'Panel Discussion',
      workshop: 'Workshop',
      break: 'Break',
      social: 'Social Event',
      registration: 'Registration',
      ceremony: 'Ceremony',
      excursion: 'Excursion',
    };
    return labels[type] || type;
  };

  const handleDownloadCalendar = () => {
    const allSessions = sessions.map((session) => ({
      title: session.title,
      start: `${session.date}T${session.time.split(' - ')[0]?.replace(':', '') || '0900'}00`,
      end: `${session.date}T${session.time.split(' - ')[1]?.replace(':', '') || '1800'}00`,
      location: session.room,
      description: session.speaker ? `Speaker: ${session.speaker}` : '',
    }));
    generateICS('CCAI-2026_Full_Schedule', allSessions);
  };

  if (loading || confLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-sky-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading conference schedule...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Schedule - {importantDetails?.ConferenceTitle?.replace(/<[^>]*>/g, '') || siteConfig?.name || 'Conference'}</title>
        <meta name="description" content="Conference program and schedule for CCAI-2026" />
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero */}
        <section className="hero-gradient py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Conference Schedule</h1>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6">
                Explore our three-day program featuring keynotes, technical sessions, and networking events
              </p>
              <Button
                onClick={handleDownloadCalendar}
                data-testid="download-schedule-btn"
                className="bg-sky-500 hover:bg-sky-600"
              >
                <Download size={18} className="mr-2" />
                Download Full Schedule (.ics)
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Schedule Tabs */}
        <section className="section-spacing">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Tabs value={activeDay} onValueChange={setActiveDay} className="w-full">
              <TabsList className="w-full justify-start mb-8 bg-gray-100 p-1 rounded-xl" data-testid="schedule-tabs">
                {conferenceDates.map((date, index) => (
                  <TabsTrigger
                    key={index + 1}
                    value={(index + 1).toString()}
                    data-testid={`tab-day-${index + 1}`}
                    className="flex-1 data-[state=active]:bg-white data-[state=active]:text-sky-600 rounded-lg px-4 py-2"
                  >
                    <div className="text-center">
                      <div className="font-semibold">Day {index + 1}</div>
                      <div className="text-xs text-gray-500">
                        {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* Dynamic Day Content */}
              {conferenceDates.map((date, dayIndex) => {
                const dayNumber = dayIndex + 1;
                return (
                  <TabsContent key={dayNumber} value={dayNumber.toString()} className="space-y-4" data-testid={`schedule-day-${dayNumber}`}>
                    {dayNumber === 1 && (
                      <>
                        <div className="card-hover bg-white rounded-2xl border-2 border-gray-100 shadow-sm overflow-hidden">
                          <div className="flex flex-col md:flex-row">
                            <div className="md:w-48 p-6 flex flex-col justify-center bg-sky-50 border-r-4 border-sky-500">
                              <div className="flex items-center gap-2 text-gray-700 mb-2">
                                <Clock size={18} />
                                <span className="font-semibold">9:00 AM - 10:00 AM</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600 text-sm">
                                <MapPin size={16} />
                                <span>Main Auditorium</span>
                              </div>
                            </div>
                            <div className="flex-1 p-6">
                              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                <h3 className="text-xl font-semibold text-gray-900">Opening Ceremony & Welcome</h3>
                                <Badge className="bg-sky-500 text-white">Ceremony</Badge>
                              </div>
                              <p className="text-gray-700 mb-2">
                                <span className="font-medium">Chair:</span> Conference Organizing Committee
                              </p>
                              <div className="mt-3">
                                <p className="text-sm font-medium text-gray-700 mb-2">Description:</p>
                                <div className="text-sm text-gray-600">
                                  Welcome address, conference overview, and keynote introduction
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="card-hover bg-white rounded-2xl border-2 border-gray-100 shadow-sm overflow-hidden">
                          <div className="flex flex-col md:flex-row">
                            <div className="md:w-48 p-6 flex flex-col justify-center bg-blue-50 border-r-4 border-blue-500">
                              <div className="flex items-center gap-2 text-gray-700 mb-2">
                                <Clock size={18} />
                                <span className="font-semibold">10:30 AM - 12:00 PM</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600 text-sm">
                                <MapPin size={16} />
                                <span>Room A</span>
                              </div>
                            </div>
                            <div className="flex-1 p-6">
                              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                <h3 className="text-xl font-semibold text-gray-900">Structural Engineering: Advanced Analysis Methods</h3>
                                <Badge className="bg-blue-500 text-white">Technical Session</Badge>
                              </div>
                              <p className="text-gray-700 mb-2">
                                <span className="font-medium">Chair:</span> Dr. Sarah Johnson
                              </p>
                              <p className="text-gray-600 text-sm mb-3">
                                <span className="font-medium">Track:</span> Structural Engineering
                              </p>
                              <div className="mt-3">
                                <p className="text-sm font-medium text-gray-700 mb-2">Featured Papers:</p>
                                <div className="text-sm text-gray-600">
                                  • Seismic Analysis of High-Rise Buildings<br/>
                                  • Advanced Composite Materials in Construction<br/>
                                  • Finite Element Modeling Techniques
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="card-hover bg-white rounded-2xl border-2 border-gray-100 shadow-sm overflow-hidden">
                          <div className="flex flex-col md:flex-row">
                            <div className="md:w-48 p-6 flex flex-col justify-center bg-green-50 border-r-4 border-green-500">
                              <div className="flex items-center gap-2 text-gray-700 mb-2">
                                <Clock size={18} />
                                <span className="font-semibold">2:00 PM - 4:00 PM</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600 text-sm">
                                <MapPin size={16} />
                                <span>Workshop Room 1</span>
                              </div>
                            </div>
                            <div className="flex-1 p-6">
                              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                <h3 className="text-xl font-semibold text-gray-900">BIM & Digital Design Workshop</h3>
                                <Badge className="bg-green-500 text-white">Workshop</Badge>
                              </div>
                              <p className="text-gray-700 mb-2">
                                <span className="font-medium">Instructor:</span> Prof. Michael Chen
                              </p>
                              <p className="text-gray-600 text-sm mb-3">
                                <span className="font-medium">Track:</span> Architecture
                              </p>
                              <div className="mt-3">
                                <p className="text-sm font-medium text-gray-700 mb-2">Description:</p>
                                <div className="text-sm text-gray-600">
                                  Hands-on workshop covering Building Information Modeling, digital design tools, and collaborative workflows
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {dayNumber === 2 && (
                      <>
                        <div className="card-hover bg-white rounded-2xl border-2 border-gray-100 shadow-sm overflow-hidden">
                          <div className="flex flex-col md:flex-row">
                            <div className="md:w-48 p-6 flex flex-col justify-center bg-red-50 border-r-4 border-red-500">
                              <div className="flex items-center gap-2 text-gray-700 mb-2">
                                <Clock size={18} />
                                <span className="font-semibold">9:00 AM - 10:30 AM</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600 text-sm">
                                <MapPin size={16} />
                                <span>Main Auditorium</span>
                              </div>
                            </div>
                            <div className="flex-1 p-6">
                              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                <h3 className="text-xl font-semibold text-gray-900">Industry Leaders Panel: Future of Infrastructure</h3>
                                <Badge className="bg-red-500 text-white">Keynote</Badge>
                              </div>
                              <p className="text-gray-700 mb-2">
                                <span className="font-medium">Moderator:</span> Dr. Emily Rodriguez
                              </p>
                              <div className="mt-3">
                                <p className="text-sm font-medium text-gray-700 mb-2">Panelists:</p>
                                <div className="text-sm text-gray-600">
                                  • CEO, Global Engineering Solutions<br/>
                                  • Director, Sustainable Infrastructure Initiative<br/>
                                  • Professor, Civil Engineering Department
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="card-hover bg-white rounded-2xl border-2 border-gray-100 shadow-sm overflow-hidden">
                          <div className="flex flex-col md:flex-row">
                            <div className="md:w-48 p-6 flex flex-col justify-center bg-purple-50 border-r-4 border-purple-500">
                              <div className="flex items-center gap-2 text-gray-700 mb-2">
                                <Clock size={18} />
                                <span className="font-semibold">11:00 AM - 12:30 PM</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600 text-sm">
                                <MapPin size={16} />
                                <span>Room B</span>
                              </div>
                            </div>
                            <div className="flex-1 p-6">
                              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                <h3 className="text-xl font-semibold text-gray-900">Geotechnical Engineering Track</h3>
                                <Badge className="bg-purple-500 text-white">Technical Session</Badge>
                              </div>
                              <p className="text-gray-700 mb-2">
                                <span className="font-medium">Chair:</span> Dr. Robert Kim
                              </p>
                              <p className="text-gray-600 text-sm mb-3">
                                <span className="font-medium">Track:</span> Geotechnical Engineering
                              </p>
                              <div className="mt-3">
                                <p className="text-sm font-medium text-gray-700 mb-2">Featured Papers:</p>
                                <div className="text-sm text-gray-600">
                                  • Ground Improvement Techniques<br/>
                                  • Slope Stability Analysis<br/>
                                  • Foundation Design Innovations
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="card-hover bg-white rounded-2xl border-2 border-gray-100 shadow-sm overflow-hidden">
                          <div className="flex flex-col md:flex-row">
                            <div className="md:w-48 p-6 flex flex-col justify-center bg-orange-50 border-r-4 border-orange-500">
                              <div className="flex items-center gap-2 text-gray-700 mb-2">
                                <Clock size={18} />
                                <span className="font-semibold">2:00 PM - 3:30 PM</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600 text-sm">
                                <MapPin size={16} />
                                <span>Room C</span>
                              </div>
                            </div>
                            <div className="flex-1 p-6">
                              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                <h3 className="text-xl font-semibold text-gray-900">Transportation Engineering Track</h3>
                                <Badge className="bg-orange-500 text-white">Technical Session</Badge>
                              </div>
                              <p className="text-gray-700 mb-2">
                                <span className="font-medium">Chair:</span> Prof. Lisa Wang
                              </p>
                              <p className="text-gray-600 text-sm mb-3">
                                <span className="font-medium">Track:</span> Transportation Engineering
                              </p>
                              <div className="mt-3">
                                <p className="text-sm font-medium text-gray-700 mb-2">Featured Papers:</p>
                                <div className="text-sm text-gray-600">
                                  • Smart Transportation Systems<br/>
                                  • Sustainable Urban Mobility<br/>
                                  • Traffic Flow Optimization
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="card-hover bg-white rounded-2xl border-2 border-gray-100 shadow-sm overflow-hidden">
                          <div className="flex flex-col md:flex-row">
                            <div className="md:w-48 p-6 flex flex-col justify-center bg-yellow-50 border-r-4 border-yellow-500">
                              <div className="flex items-center gap-2 text-gray-700 mb-2">
                                <Clock size={18} />
                                <span className="font-semibold">3:00 PM - 5:00 PM</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600 text-sm">
                                <MapPin size={16} />
                                <span>Exhibit Hall</span>
                              </div>
                            </div>
                            <div className="flex-1 p-6">
                              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                <h3 className="text-xl font-semibold text-gray-900">Poster Presentations & Networking</h3>
                                <Badge className="bg-yellow-500 text-white">Social Event</Badge>
                              </div>
                              <p className="text-gray-700 mb-2">
                                <span className="font-medium">Coordinator:</span> Student Affairs Committee
                              </p>
                              <div className="mt-3">
                                <p className="text-sm font-medium text-gray-700 mb-2">Description:</p>
                                <div className="text-sm text-gray-600">
                                  Student research showcase, poster presentations, and networking reception with refreshments
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {dayNumber === 3 && (
                      <>
                        <div className="card-hover bg-white rounded-2xl border-2 border-gray-100 shadow-sm overflow-hidden">
                          <div className="flex flex-col md:flex-row">
                            <div className="md:w-48 p-6 flex flex-col justify-center bg-teal-50 border-r-4 border-teal-500">
                              <div className="flex items-center gap-2 text-gray-700 mb-2">
                                <Clock size={18} />
                                <span className="font-semibold">9:00 AM - 12:00 PM</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600 text-sm">
                                <MapPin size={16} />
                                <span>Workshop Room 2</span>
                              </div>
                            </div>
                            <div className="flex-1 p-6">
                              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                <h3 className="text-xl font-semibold text-gray-900">Sustainable Infrastructure Workshop</h3>
                                <Badge className="bg-teal-500 text-white">Workshop</Badge>
                              </div>
                              <p className="text-gray-700 mb-2">
                                <span className="font-medium">Instructor:</span> Dr. Maria Gonzalez
                              </p>
                              <p className="text-gray-600 text-sm mb-3">
                                <span className="font-medium">Track:</span> Infrastructure
                              </p>
                              <div className="mt-3">
                                <p className="text-sm font-medium text-gray-700 mb-2">Description:</p>
                                <div className="text-sm text-gray-600">
                                  Interactive workshop on sustainable design principles, green materials, and environmental impact assessment
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="card-hover bg-white rounded-2xl border-2 border-gray-100 shadow-sm overflow-hidden">
                          <div className="flex flex-col md:flex-row">
                            <div className="md:w-48 p-6 flex flex-col justify-center bg-indigo-50 border-r-4 border-indigo-500">
                              <div className="flex items-center gap-2 text-gray-700 mb-2">
                                <Clock size={18} />
                                <span className="font-semibold">1:00 PM - 2:30 PM</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600 text-sm">
                                <MapPin size={16} />
                                <span>Main Auditorium</span>
                              </div>
                            </div>
                            <div className="flex-1 p-6">
                              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                <h3 className="text-xl font-semibold text-gray-900">Closing Ceremony & Awards</h3>
                                <Badge className="bg-indigo-500 text-white">Ceremony</Badge>
                              </div>
                              <p className="text-gray-700 mb-2">
                                <span className="font-medium">Chair:</span> Conference Organizing Committee
                              </p>
                              <div className="mt-3">
                                <p className="text-sm font-medium text-gray-700 mb-2">Description:</p>
                                <div className="text-sm text-gray-600">
                                  Best paper awards, conference summary, and future directions discussion
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="card-hover bg-white rounded-2xl border-2 border-gray-100 shadow-sm overflow-hidden">
                          <div className="flex flex-col md:flex-row">
                            <div className="md:w-48 p-6 flex flex-col justify-center bg-pink-50 border-r-4 border-pink-500">
                              <div className="flex items-center gap-2 text-gray-700 mb-2">
                                <Clock size={18} />
                                <span className="font-semibold">4:00 PM - 6:00 PM</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600 text-sm">
                                <MapPin size={16} />
                                <span>Conference Center Terrace</span>
                              </div>
                            </div>
                            <div className="flex-1 p-6">
                              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                <h3 className="text-xl font-semibold text-gray-900">Cocktail Reception & Farewell</h3>
                                <Badge className="bg-pink-500 text-white">Social Event</Badge>
                              </div>
                              <p className="text-gray-700 mb-2">
                                <span className="font-medium">Host:</span> Conference Organizing Committee
                              </p>
                              <div className="mt-3">
                                <p className="text-sm font-medium text-gray-700 mb-2">Description:</p>
                                <div className="text-sm text-gray-600">
                                  Networking reception with cocktails, light refreshments, and opportunity to connect with fellow attendees
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {dayNumber > 3 && (
                      <div className="card-hover bg-white rounded-2xl border-2 border-gray-100 shadow-sm overflow-hidden">
                        <div className="flex flex-col md:flex-row">
                          <div className="md:w-48 p-6 flex flex-col justify-center bg-gray-50 border-r-4 border-gray-500">
                            <div className="flex items-center gap-2 text-gray-700 mb-2">
                              <Clock size={18} />
                              <span className="font-semibold">9:00 AM - 5:00 PM</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600 text-sm">
                              <MapPin size={16} />
                              <span>Various Rooms</span>
                            </div>
                          </div>
                          <div className="flex-1 p-6">
                            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                              <h3 className="text-xl font-semibold text-gray-900">Full Day Conference Sessions</h3>
                              <Badge className="bg-gray-500 text-white">Conference Day</Badge>
                            </div>
                            <div className="mt-3">
                              <p className="text-sm font-medium text-gray-700 mb-2">Description:</p>
                              <div className="text-sm text-gray-600">
                                Technical presentations, workshops, and networking sessions throughout the day
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </TabsContent>
                );
              })}
            </Tabs>
          </div>
        </section>

        {/* Legend */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Track Legend</h3>
            <div className="flex flex-wrap gap-3">
              {(siteConfig?.tracks || [
                { id: 'structural', name: 'Structural Engineering', color: '#0ea5e9' },
                { id: 'geotechnical', name: 'Geotechnical Engineering', color: '#8b5cf6' },
                { id: 'transportation', name: 'Transportation Engineering', color: '#f59e0b' },
                { id: 'water', name: 'Water Resources', color: '#06b6d4' },
                { id: 'construction', name: 'Construction Management', color: '#10b981' },
                { id: 'environmental', name: 'Environmental Engineering', color: '#84cc16' },
              ]).map((track) => (
                <div key={track.id} className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: track.color }}
                  />
                  <span className="text-sm text-gray-700">{track.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SchedulePage;
